import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Trash2, Download, Upload, Play, Plus, RotateCcw, Star, Pencil, Eye, Search, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import { Skill, Role, ProjectType, Project, Formation, Profile } from '../types';
import { localized, titleOf } from '../utils';
import PageMeta from '../components/PageMeta';
import { FormSkeleton } from '../components/Skeletons';
import ProfileForm from '../components/forms/ProfileForm';
import ReferencesTab from '../components/ReferencesTab';
import { BACKEND_URL } from '../config';

const QUICK_QUERIES: [string, string][] = [
  ['q_private', 'SELECT title, strid FROM project WHERE is_public = false;'],
  ['q_unused_skills', "SELECT s.id, s.name->>'fr' AS skill FROM skill s WHERE NOT EXISTS (SELECT 1 FROM project_skill WHERE id_skill = s.id) AND NOT EXISTS (SELECT 1 FROM formation_skill WHERE id_skill = s.id) ORDER BY 2;"],
  ['q_no_banner', "SELECT title, strid FROM project WHERE banner IS NULL OR banner = '';"],
  ['q_by_type', "SELECT pt.name->>'fr' AS type, COUNT(p.id) AS projects FROM project_type pt LEFT JOIN project p ON p.type_id = pt.id GROUP BY pt.id ORDER BY 2 DESC;"],
];

const TABS = ['projects', 'formations', 'profile', 'references', 'data'] as const;
type Tab = (typeof TABS)[number];

const input = 'h-11 border border-zinc-700 bg-base text-ink placeholder:text-zinc-500 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40';
const primaryBtn = 'h-11 px-4 inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50';
const ghostBtn = 'h-9 px-3 inline-flex items-center gap-1.5 rounded-lg text-sm text-zinc-400 hover:text-ink hover:bg-surface transition-colors';
const th = 'px-4 py-3 text-left text-xs font-medium text-zinc-500 whitespace-nowrap';
const td = 'px-4 py-2.5';

function Section({ title, children, aside }: { title: string; children: React.ReactNode; aside?: React.ReactNode }) {
  return (
    <section className="mb-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-display text-2xl font-extrabold tracking-[-0.03em] text-ink">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Switch({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={onClick}
      className={`relative w-11 h-6 rounded-full transition-colors ${on ? 'bg-primary' : 'bg-zinc-700'}`}>
      <span className={`absolute top-[3px] w-[18px] h-[18px] rounded-full bg-white transition-all ${on ? 'left-[23px]' : 'left-[3px]'}`} />
    </button>
  );
}

export default function Admin() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = (TABS as readonly string[]).includes(params.get('tab') ?? '') ? (params.get('tab') as Tab) : 'projects';
  const [projects, setProjects] = useState<Project[]>([]);
  const [formations, setFormations] = useState<Formation[]>([]);
  const [profile, setProfile] = useState<Profile | null | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<'all' | 'experience' | 'study'>('all');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [admins, setAdmins] = useState<string[]>([]);
  const [addingAccount, setAddingAccount] = useState(false);
  const [dbFile, setDbFile] = useState<File | null>(null);
  const [sqlQuery, setSqlQuery] = useState('');
  const [sqlResult, setSqlResult] = useState<{ command?: string; rowCount?: number; rows?: Record<string, any>[]; fields?: string[]; error?: string } | null>(null);
  const [sqlLoading, setSqlLoading] = useState(false);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const token = localStorage.getItem('token');
  const auth: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${BACKEND_URL}/api/projects`, { headers }).then(r => r.json()).then(d => setProjects(Array.isArray(d) ? d : [])).catch(console.error);
    fetch(`${BACKEND_URL}/api/formations`, { headers }).then(r => r.json()).then(d => setFormations(Array.isArray(d) ? d : [])).catch(console.error);
    fetch(`${BACKEND_URL}/api/profile`).then(r => r.json()).then(setProfile).catch(() => setProfile(null));
    fetch(`${BACKEND_URL}/api/skills`).then(r => r.json()).then(setSkills).catch(console.error);
    fetch(`${BACKEND_URL}/api/roles`).then(r => r.json()).then(setRoles).catch(console.error);
    fetch(`${BACKEND_URL}/api/project-types`).then(r => r.json()).then(setProjectTypes).catch(console.error);
  }, [token]);

  const [chat, setChat] = useState<{ logs: { id: number; question: string; lang: string; sources: { title: string; url: string }[]; created_at: string }[]; chunks: number } | null>(null);
  const [reindexing, setReindexing] = useState(false);
  const loadChat = () =>
    fetch(`${BACKEND_URL}/api/chat/logs`, { headers: auth }).then(r => r.json()).then(d => setChat(Array.isArray(d.logs) ? d : null)).catch(console.error);
  const handleReindex = async () => {
    setReindexing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/chat/reindex`, { method: 'POST', headers: auth });
      if (!res.ok) throw new Error();
      toast.success(t('chat_reindexed') as string);
      loadChat();
    } catch {
      toast.error(t('chat_reindex_failed') as string);
    } finally {
      setReindexing(false);
    }
  };

  const loadAdmins = () =>
    fetch(`${BACKEND_URL}/api/admins`, { headers: auth }).then(r => r.json()).then(d => setAdmins(Array.isArray(d) ? d : [])).catch(console.error);

  useEffect(() => { if (tab === 'data') { loadAdmins(); loadChat(); } }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Projects / formations ────────────────────────────────────────────────

  const setFlag = async (p: Project, flag: 'is_public' | 'featured') => {
    const value = !p[flag];
    setProjects(prev => prev.map(x => x.strid === p.strid ? { ...x, [flag]: value } : x));
    try {
      const res = await fetch(`${BACKEND_URL}/api/projects/${p.strid}/flags`, {
        method: 'PATCH',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [flag]: value }),
      });
      if (!res.ok) throw new Error();
    } catch {
      setProjects(prev => prev.map(x => x.strid === p.strid ? { ...x, [flag]: !value } : x));
      toast.error(t('error_saving'));
    }
  };

  const showcase = useMemo(
    () => projects.filter(p => p.featured).sort((a, b) => (a.featured_order ?? 1e9) - (b.featured_order ?? 1e9)),
    [projects]
  );
  const [dragFrom, setDragFrom] = useState<number | null>(null);

  const moveShowcase = async (from: number, to: number) => {
    if (to < 0 || to >= showcase.length || from === to) return;
    const ids = showcase.map(p => p.strid);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    const before = projects;
    setProjects(prev => prev.map(x => ids.includes(x.strid) ? { ...x, featured_order: ids.indexOf(x.strid) + 1 } : x));
    const res = await fetch(`${BACKEND_URL}/api/projects/order/featured`, {
      method: 'PUT', headers: { ...auth, 'Content-Type': 'application/json' }, body: JSON.stringify({ strids: ids }),
    }).catch(() => null);
    if (!res?.ok) { setProjects(before); toast.error(t('error_saving')); }
  };

  const deleteProject = async (strid: string) => {
    if (!window.confirm(t('confirm_delete_project') as string)) return;
    const res = await fetch(`${BACKEND_URL}/api/projects/${strid}`, { method: 'DELETE', headers: auth }).catch(() => null);
    if (res?.ok) setProjects(prev => prev.filter(p => p.strid !== strid)); else toast.error(t('error_saving'));
  };

  const deleteFormation = async (strid: string) => {
    if (!window.confirm(t('confirm_delete_formation') as string)) return;
    const res = await fetch(`${BACKEND_URL}/api/formations/${strid}`, { method: 'DELETE', headers: auth }).catch(() => null);
    if (res?.ok) setFormations(prev => prev.filter(f => f.strid !== strid)); else toast.error(t('error_saving'));
  };

  const q = search.trim().toLowerCase();
  const visibleProjects = projects.filter(p => !q || titleOf(p, lang).toLowerCase().includes(q) || localized(p.type, lang).toLowerCase().includes(q));
  const visibleFormations = formations.filter(f => (kind === 'all' || (kind === 'study') === !!f.is_study) && (!q || f.name.toLowerCase().includes(q) || localized(f.position, lang).toLowerCase().includes(q) || localized(f.degree, lang).toLowerCase().includes(q)));

  // ── Data ─────────────────────────────────────────────────────────────────

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/register`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (data.error) { toast.error(data.error); return; }
      toast.success(data.message);
      setUsername(''); setPassword(''); setAddingAccount(false);
      loadAdmins();
    } catch {
      toast.error(t('error_creating_account') as string);
    }
  };

  const handleDeleteAccount = async (name: string) => {
    if (!token || !window.confirm(t('confirm_delete_account') as string)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/${encodeURIComponent(name)}`, { method: 'DELETE', headers: auth });
      const data = await res.json();
      if (data.error) { toast.error(data.error); return; }
      if (name === localStorage.getItem('admin')) {
        localStorage.removeItem('token');
        localStorage.removeItem('admin');
        navigate('/');
      } else {
        loadAdmins();
      }
    } catch {
      toast.error(t('error_deleting_account') as string);
    }
  };

  const handleSql = async () => {
    if (!token || !sqlQuery.trim()) return;
    setSqlLoading(true);
    setSqlResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/sql`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: sqlQuery }),
      });
      const data = await res.json();
      if (!res.ok) setSqlResult({ error: data.error });
      else setSqlResult(data);
    } catch {
      setSqlResult({ error: t('network_error') as string });
    } finally {
      setSqlLoading(false);
    }
  };

  const handleExport = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/export-database`, { headers: auth });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'portfolio-backup.zip';
      link.click();
    } catch {
      toast.error(t('error_exporting_database') as string);
    }
  };

  const handleImport = async () => {
    if (!token || !dbFile) return;
    const fd = new FormData();
    fd.append('file', dbFile);
    try {
      await fetch(`${BACKEND_URL}/api/import-database`, { method: 'POST', headers: auth, body: fd });
      toast.success(t('database_imported') as string);
      setDbFile(null);
    } catch {
      toast.error(t('error_importing_database') as string);
    }
  };

  const handleReset = async () => {
    if (!token || !window.confirm(t('confirm_reset_database') as string)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/reset-database`, { method: 'POST', headers: auth });
      const data = await res.json();
      if (data.error) toast.error(data.error); else toast.success(data.message);
    } catch {
      toast.error(t('error_resetting_database') as string);
    }
  };

  const tabLabels: Record<Tab, string> = {
    projects: `${t('projects')}`,
    formations: `${t('career')}`,
    profile: `${t('profile')}`,
    references: `${t('references')}`,
    data: `${t('data')}`,
  };
  const counts: Partial<Record<Tab, number>> = { projects: projects.length, formations: formations.length, references: skills.length };

  const searchBox = (
    <label className="relative block w-full sm:w-72">
      <span className="sr-only">{t('search')}</span>
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
      <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('search') as string} className={`${input} w-full pl-9`} />
    </label>
  );

  return (
    <div className="min-h-screen pt-24 pb-16">
      <PageMeta title={t('admin') as string} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-[-0.04em] mb-6">Admin</h1>
        <nav aria-label="Admin" className="flex flex-wrap gap-x-1 border-b border-line mb-8">
          {TABS.map(id => (
            <button key={id} onClick={() => setParams({ tab: id })} aria-current={tab === id ? 'page' : undefined}
              className={`h-11 px-4 whitespace-nowrap text-sm font-medium border-b-2 -mb-px transition-colors ${tab === id ? 'border-ink text-ink' : 'border-transparent text-zinc-500 hover:text-ink'}`}>
              {tabLabels[id]} {counts[id] !== undefined && <span className="text-xs text-zinc-500 ml-0.5">{counts[id]}</span>}
            </button>
          ))}
        </nav>

        {tab === 'projects' && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              {searchBox}
              <Link to="/projects/new" className={primaryBtn}><Plus size={16} /> {t('new_project')}</Link>
            </div>
            {showcase.length > 1 && (
              <div className="mb-6 border border-line rounded-xl p-4">
                <p className="text-xs font-medium text-zinc-500 mb-3">{t('showcase_order')}</p>
                <ol className="flex flex-col gap-2">
                  {showcase.map((p, i) => (
                    <li key={p.strid} draggable
                      onDragStart={() => setDragFrom(i)} onDragOver={e => e.preventDefault()}
                      onDrop={() => { if (dragFrom !== null) moveShowcase(dragFrom, i); setDragFrom(null); }}
                      className="flex items-center gap-2 h-11 px-3 rounded-lg bg-surface text-sm cursor-grab active:cursor-grabbing">
                      <GripVertical size={16} className="text-zinc-500" aria-hidden />
                      <span className="w-5 text-xs text-zinc-500">{i + 1}</span>
                      <span className="flex-1 font-semibold text-ink truncate">{titleOf(p, lang)}</span>
                      <button type="button" onClick={() => moveShowcase(i, i - 1)} disabled={i === 0} aria-label="↑" className={`${ghostBtn} !px-2 disabled:opacity-30`}><ArrowUp size={14} /></button>
                      <button type="button" onClick={() => moveShowcase(i, i + 1)} disabled={i === showcase.length - 1} aria-label="↓" className={`${ghostBtn} !px-2 disabled:opacity-30`}><ArrowDown size={14} /></button>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <div className="overflow-x-auto border border-line rounded-xl">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-surface"><tr>
                  <th className={th}>{t('showcase')}</th><th className={th}>{t('title')}</th><th className={th}>{t('type')}</th>
                  <th className={th}>{t('start_date')}</th><th className={th}>{t('public')}</th><th className={th}><span className="sr-only">Actions</span></th>
                </tr></thead>
                <tbody>
                  {visibleProjects.map(p => (
                    <tr key={p.strid} className="border-t border-line">
                      <td className={td}>
                        <button type="button" onClick={() => setFlag(p, 'featured')} aria-pressed={!!p.featured} aria-label={t('showcase') as string}
                          className={`w-10 h-10 rounded-lg flex items-center justify-center hover:bg-surface ${p.featured ? 'text-primary-light' : 'text-zinc-500'}`}>
                          <Star size={18} className={p.featured ? 'fill-current' : ''} />
                        </button>
                      </td>
                      <td className={td}><Link to={`/projects/${p.strid}`} className="font-semibold text-ink hover:text-primary-light hover:underline">{titleOf(p, lang)}</Link></td>
                      <td className={`${td} text-zinc-400`}>{localized(p.type, lang)}</td>
                      <td className={`${td} text-zinc-400 font-mono text-xs`}>{p.start_date ?? '—'}</td>
                      <td className={td}><Switch on={p.is_public} label={t('public') as string} onClick={() => setFlag(p, 'is_public')} /></td>
                      <td className={`${td} text-right whitespace-nowrap`}>
                        <Link to={`/projects/${p.strid}`} className={ghostBtn}><Eye size={14} /> {t('view')}</Link>
                        <Link to={`/projects/edit/${p.strid}`} className={ghostBtn}><Pencil size={14} /> {t('edit')}</Link>
                        <button onClick={() => deleteProject(p.strid)} className={`${ghostBtn} hover:!text-red-500`} aria-label={`${t('delete')} ${titleOf(p, lang)}`}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'formations' && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              {searchBox}
              <div className="flex flex-wrap gap-2">
                <button onClick={() => navigate('/formations/new', { state: { createStudy: false } })} className={primaryBtn}><Plus size={16} /> {t('experiences_title')}</button>
                <button onClick={() => navigate('/formations/new', { state: { createStudy: true } })} className={primaryBtn}><Plus size={16} /> {t('formations')}</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label={t('type') as string}>
              {([['all', t('all'), formations.length], ['experience', t('experiences_title'), formations.filter(f => !f.is_study).length], ['study', t('formations'), formations.filter(f => f.is_study).length]] as const).map(([id, label, n]) => (
                <button key={id} type="button" onClick={() => setKind(id)} aria-pressed={kind === id}
                  className={`h-9 px-4 rounded-full text-[13px] font-medium border transition-colors ${kind === id ? 'bg-primary text-white border-primary' : 'border-line text-ink hover:bg-surface'}`}>
                  {label} <span className="opacity-70">{n}</span>
                </button>
              ))}
            </div>
            <div className="overflow-x-auto border border-line rounded-xl">
              <table className="w-full min-w-[680px] text-sm">
                <thead className="bg-surface"><tr>
                  <th className={th}>{t('title')}</th><th className={th}>{t('type')}</th><th className={th}>{t('dates')}</th><th className={th}><span className="sr-only">Actions</span></th>
                </tr></thead>
                <tbody>
                  {visibleFormations.map(f => (
                    <tr key={f.strid} className="border-t border-line">
                      <td className={td}>
                        <Link to={`/formations#${f.strid}`} className="block font-semibold text-ink hover:text-primary-light hover:underline">{(f.is_study ? localized(f.degree, lang) : localized(f.position, lang)) || f.name}</Link>
                        <span className="text-xs text-zinc-500">{f.name}</span>
                      </td>
                      <td className={`${td} text-zinc-400`}>{f.is_study ? t('formations') : t('experiences_title')}</td>
                      <td className={`${td} text-zinc-400 font-mono text-xs whitespace-nowrap`}>{f.start_date} → {f.ongoing ? t('on_going') : f.end_date}</td>
                      <td className={`${td} text-right whitespace-nowrap`}>
                        <Link to={`/formations#${f.strid}`} className={ghostBtn}><Eye size={14} /> {t('view')}</Link>
                        <Link to={`/formations/edit/${f.strid}`} className={ghostBtn}><Pencil size={14} /> {t('edit')}</Link>
                        <button onClick={() => deleteFormation(f.strid)} className={`${ghostBtn} hover:!text-red-500`} aria-label={`${t('delete')} ${f.name}`}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'profile' && (
          <div>
            {profile === undefined
              ? <FormSkeleton />
              : <ProfileForm profile={profile} onSaved={setProfile} />}
          </div>
        )}

        {tab === 'references' && (
          <ReferencesTab auth={auth} skills={skills} setSkills={setSkills} roles={roles} setRoles={setRoles}
            projectTypes={projectTypes} setProjectTypes={setProjectTypes} projects={projects} formations={formations} />
        )}

        {tab === 'data' && (
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] items-start">
            <div className="min-w-0">
            <Section title={t('sql_console')}>
              <div className="flex flex-wrap gap-2 mb-3">
                {QUICK_QUERIES.map(([label, query]) => (
                  <button key={label} type="button" onClick={() => setSqlQuery(query)}
                    className="h-9 px-3.5 rounded-full border border-line text-[13px] font-medium text-ink hover:bg-surface transition-colors">{t(label)}</button>
                ))}
              </div>
              <textarea
                className="w-full border border-line bg-base text-ink rounded-lg px-4 py-3 font-mono text-sm resize-y min-h-[160px] focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-zinc-500"
                placeholder={t('sql_placeholder') as string}
                aria-label={t('sql_console') as string}
                value={sqlQuery}
                onChange={e => setSqlQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleSql(); } }}
              />
              <button onClick={handleSql} disabled={sqlLoading || !sqlQuery.trim()} className={`${primaryBtn} mt-3`}>
                <Play size={14} /> {sqlLoading ? '...' : t('sql_execute')} <span className="text-white/60 text-xs">(Ctrl+Enter)</span>
              </button>
              {sqlResult && (
                <div className="mt-4">
                  {sqlResult.error ? (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-500 font-mono whitespace-pre-wrap">{sqlResult.error}</div>
                  ) : (
                    <div>
                      <p className="text-xs text-zinc-500 mb-2">{sqlResult.command} — {sqlResult.rowCount} {t('sql_rows_affected')}</p>
                      {sqlResult.rows && sqlResult.rows.length > 0 && (
                        <div className="overflow-auto max-h-[420px] border border-line rounded-lg">
                          <table className="w-full text-xs font-mono">
                            <thead><tr className="bg-surface sticky top-0">
                              {sqlResult.fields?.map(f => <th key={f} className="px-3 py-2 text-left text-zinc-400 font-semibold whitespace-nowrap">{f}</th>)}
                            </tr></thead>
                            <tbody>
                              {sqlResult.rows.map((row, i) => (
                                <tr key={i} className="border-t border-line">
                                  {sqlResult.fields?.map(f => (
                                    <td key={f} className="px-3 py-1.5 text-zinc-300 whitespace-nowrap max-w-[300px] truncate">
                                      {row[f] === null ? <span className="text-zinc-500">NULL</span> : typeof row[f] === 'object' ? JSON.stringify(row[f]) : String(row[f])}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Section>

            <Section title={t('chat_questions')} aside={
              <button type="button" onClick={handleReindex} disabled={reindexing} className={ghostBtn}>
                <RotateCcw size={14} /> {reindexing ? '...' : t('chat_reindex')}{chat && <span className="text-zinc-500">· {chat.chunks} {t('chat_chunks')}</span>}
              </button>
            }>
              {chat && chat.logs.length > 0 ? (
                <ul className="divide-y divide-line border-y border-line max-h-[420px] overflow-auto">
                  {chat.logs.map(l => (
                    <li key={l.id} className="py-2.5 text-sm">
                      <p className="text-ink">{l.question}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {new Date(l.created_at).toLocaleString(lang)} · {l.lang.toUpperCase()}{l.sources.length > 0 && <> · {l.sources.map(s => s.title).join(', ')}</>}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-zinc-500">{t('chat_no_questions')}</p>}
            </Section>
            </div>

            <aside className="space-y-6">
              <div className="rounded-2xl border border-line p-5 space-y-3">
                <h2 className="font-display text-lg font-extrabold tracking-[-0.02em] text-ink">{t('database_manager')}</h2>
                <button onClick={handleExport} className="w-full min-h-[44px] border border-line rounded-lg text-ink hover:bg-surface transition-colors text-sm flex items-center justify-center gap-2">
                  <Download size={16} /> {t('export_database')}
                </button>
                <label className="min-h-[44px] flex items-center justify-center gap-2 border border-dashed border-zinc-700 rounded-lg px-3 cursor-pointer hover:bg-surface transition-colors text-sm text-zinc-400">
                  <Upload size={16} /> {dbFile ? dbFile.name : t('import_database')}
                  <input type="file" accept=".zip" className="hidden" onChange={e => setDbFile(e.target.files?.[0] || null)} />
                </label>
                {dbFile && <button onClick={handleImport} className={`${primaryBtn} w-full`}>{t('import_database')}</button>}
              </div>

              <div className="rounded-2xl border border-line p-5">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h2 className="font-display text-lg font-extrabold tracking-[-0.02em] text-ink">{t('accounts')}</h2>
                  {!addingAccount && (
                    <button type="button" onClick={() => setAddingAccount(true)} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-full border border-line text-[13px] font-medium text-ink hover:bg-surface transition-colors">
                      <Plus size={14} /> {t('new_account')}
                    </button>
                  )}
                </div>
                <ul className="divide-y divide-line">
                  {admins.map(name => (
                    <li key={name} className="flex items-center justify-between gap-2 py-2 text-sm text-ink">
                      <span className="truncate">{name}</span>
                      {admins.length > 1 && (
                        <button type="button" onClick={() => handleDeleteAccount(name)} className="p-2 rounded-lg text-zinc-500 hover:text-red-500 hover:bg-red-500/10 transition-colors" aria-label={`${t('delete_account')} ${name}`}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                {addingAccount && (
                  <form onSubmit={handleRegister} className="flex flex-col gap-2 mt-3 pt-3 border-t border-line">
                    <input className={input} placeholder={t('username') as string} aria-label={t('username') as string} value={username} onChange={e => setUsername(e.target.value)} required autoFocus />
                    <input className={input} type="password" placeholder={t('password') as string} aria-label={t('password') as string} value={password} onChange={e => setPassword(e.target.value)} required />
                    <div className="flex gap-2">
                      <button type="submit" className={`${primaryBtn} flex-1`}>{t('create_account')}</button>
                      <button type="button" onClick={() => { setAddingAccount(false); setUsername(''); setPassword(''); }} className="h-11 px-4 rounded-lg border border-line text-sm text-ink hover:bg-surface transition-colors">{t('cancel')}</button>
                    </div>
                  </form>
                )}
              </div>

              <details className="rounded-2xl border border-red-500/40 p-5">
                <summary className="cursor-pointer min-h-[44px] flex items-center font-display text-lg font-extrabold tracking-[-0.02em] text-red-500">{t('danger_zone')}</summary>
                <div className="flex flex-col gap-2 pt-2">
                  <button onClick={handleReset} className="min-h-[44px] flex items-center justify-center gap-2 rounded-lg border border-red-500 text-red-500 text-sm font-semibold hover:bg-red-500/10 transition-colors">
                    <RotateCcw size={16} /> {t('reset_database')}
                  </button>
                </div>
              </details>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
